import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { CALENDLY_URL } from "@/constants/site";

const LINKS = [
  { label: "About", href: "/who-am-i", testId: "nav-link-about" },
  { label: "Why Me", href: "#why-us", testId: "nav-link-why-us" },
  { label: "Capabilities", href: "#capabilities", testId: "nav-link-capabilities" },
  { label: "Insights", href: "#insights", testId: "nav-link-insights" },
  { label: "Metrics", href: "#metrics", testId: "nav-link-metrics" },
  { label: "Contact", href: "#contact", testId: "nav-link-contact" },
];

export const scrollToSection = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -64, duration: 1.4 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goHome = (hash = "") => {
    if (location.pathname !== "/") {
      // ScrollToHash in App.js lands the section (retry-corrected)
      navigate(`/${hash}`);
      return true;
    }
    return false;
  };

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (!goHome(href)) {
      scrollToSection(href);
    }
  };

  const handleLogo = (e) => {
    e.preventDefault();
    setOpen(false);
    if (!goHome()) {
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { duration: 1.4 });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-2xl border-b border-white/10"
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between h-16 lg:h-20 px-6 lg:px-12">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={handleLogo}
          className="flex items-center"
        >
          <img
            src="/tecc-lockup.png"
            alt="Tech Executive Capability Center LLC"
            data-testid="nav-logo-image"
            className="h-8 lg:h-9 w-auto"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-10" data-testid="nav-desktop">
          {LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={link.testId}
              onClick={(e) => handleNav(e, link.href)}
              className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 bg-[#E6E4D9] text-black font-mono text-xs uppercase tracking-[0.15em] px-5 py-2.5 hover:bg-[#C2C0B6] transition-colors duration-300"
          >
            Executive Briefing
            <ArrowUpRight size={14} weight="bold" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white/80 hover:text-white transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-2xl"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`${link.testId}-mobile`}
                  onClick={(e) => handleNav(e, link.href)}
                  className="font-mono text-sm uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors duration-300"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-cta-button-mobile"
                className="inline-flex items-center gap-2 bg-[#E6E4D9] text-black font-mono text-xs uppercase tracking-[0.15em] px-5 py-3 mt-2 hover:bg-[#C2C0B6] transition-colors duration-300 w-fit"
              >
                Executive Briefing
                <ArrowUpRight size={14} weight="bold" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
